import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { Dropdown, FeatureRadio } from '@/components/chariot-ui';
import { Input, InputEvent } from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import { useModifyAccount } from '@/hooks/useAccounts';
import useInitialize, {
  CloudInitializeRequest,
  CloudInitializeResponse,
} from '@/hooks/useInitialize';
import { triggerConfetti } from '@/utils/confetti.util';
import { downloadIACTemplate } from '@/utils/download.util';
import { useStorage } from '@/utils/storage/useStorage.util';

const DEV_TYPE_OPTIONS = [
  { label: 'Terraform (Recommended)', value: 'terraform' },
  { label: 'Manual', value: 'manual' },
];

type SelectionType = 'organization' | 'individual';

type FormState = {
  organization: {
    org_id: string;
    deployment_type: string;
    workload_pool_id: string;
    workload_provider_id: string;
    service_account_email: string;
  };
  individual: {
    org_id: string;
    project_id: string;
    deployment_type: string;
    workload_pool_id: string;
    workload_provider_id: string;
    service_account_email: string;
  };
};
const defaultFormState = {
  organization: {
    org_id: '',
    deployment_type: '',
    workload_pool_id: '',
    workload_provider_id: '',
    service_account_email: '',
  },
  individual: {
    org_id: '',
    project_id: '',
    deployment_type: '',
    workload_pool_id: '',
    workload_provider_id: '',
    service_account_email: '',
  },
};

export function GoogleCloud({ onClose }: { onClose: () => void }) {
  const [savedState, setSavedState] = useStorage<{
    request: CloudInitializeRequest;
    response: CloudInitializeResponse;
  }>({ key: 'google-cloud-integration-state' });

  const [selection, setSelection] = useState<SelectionType | undefined>(
    savedState
      ? savedState?.request.project_id
        ? 'individual'
        : 'organization'
      : 'organization'
  );
  const [form, setForm] = useState<FormState>(
    savedState
      ? {
          organization: {
            org_id: savedState?.request.org_id || '',
            deployment_type: savedState?.request.deployment_type || '',
            workload_pool_id: '',
            workload_provider_id: '',
            service_account_email:
              savedState?.request.service_account_email || '',
          },
          individual: {
            org_id: savedState?.request.org_id || '',
            project_id: savedState?.request.project_id || '',
            deployment_type: savedState?.request.deployment_type || '',
            workload_pool_id: '',
            workload_provider_id: '',
            service_account_email:
              savedState?.request.service_account_email || '',
          },
        }
      : defaultFormState
  );
  const [step, setStep] = useState(savedState ? 1 : 0);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [infrastructureId, setInfrastructureId] = useState('');
  const [projectId, setProjectId] = useState('');

  const { mutateAsync: initializeCloud, status: initializeCloudStatus } =
    useInitialize();
  const { mutateAsync: link, status: linkStatus } = useModifyAccount('link');

  // Trigger confetti effect when entering step 2
  useEffect(() => {
    if (step === 2) {
      triggerConfetti({ delay: 300 });
    }
  }, [step]);

  // Handle input changes
  const handleInputChange =
    (type: SelectionType, field: string) => (e: InputEvent) => {
      setForm(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          [field]: e.target.value,
        },
      }));
    };

  // Handle Dropdown selection for deployment_type
  const handleDropdownSelect =
    (type: SelectionType) => (option: { value?: string | number }) => {
      if (typeof option.value === 'string') {
        setForm(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            deployment_type: option.value,
          },
        }));
      }
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selection) return;

    const request: CloudInitializeRequest = {
      provider: 'gcp',
      org_id: form[selection].org_id,
      deployment_type: form[selection].deployment_type,
      ...(selection === 'individual' && {
        project_id: form.individual.project_id,
      }),
      ...(form[selection].workload_pool_id && {
        workload_pool_id: form[selection].workload_pool_id,
      }),
      ...(form[selection].workload_provider_id && {
        workload_provider_id: form[selection].workload_provider_id,
      }),
      ...(form[selection].service_account_email && {
        service_account_email: form[selection].service_account_email,
      }),
    };

    const response = await initializeCloud(request);
    setSavedState({
      request,
      response,
    });
    setStep(1);
  };

  // Find the selected deployment_type option for Dropdown
  const getSelectedDropdownOption = (type: SelectionType) => {
    const val = form[type].deployment_type;
    return val ? [DEV_TYPE_OPTIONS.find(opt => opt.value === val)!] : [];
  };

  const handleReset = () => {
    setSavedState(undefined);
    setSelection(undefined);
    setHasDownloaded(false);
    setForm(defaultFormState);
    setStep(0);
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const handleFinish = async () => {
    if (form[selection!].deployment_type !== 'manual' && !hasDownloaded) {
      if (savedState?.response?.template) {
        const point =
          selection === 'organization'
            ? form.organization.org_id
            : form.individual.project_id;

        downloadIACTemplate({
          template: savedState.response.template,
          filename: `guard-gcp-${point}`,
          deploymentType: form[selection!].deployment_type,
        });
        setHasDownloaded(true);
      }
    } else {
      const org_id = form[selection!].org_id;

      try {
        await link({
          username: 'gcp',
          config: {
            org_id,
            infra_project_id: infrastructureId,
            cognito_username: savedState?.response.username,
            cognito_password: savedState?.response.password,
            // For organization integrations, use the projectId from Terraform output
            // For individual integrations, use the form's project_id
            project_id:
              selection === 'organization'
                ? projectId
                : form.individual.project_id,
            ...(form[selection!].workload_pool_id && {
              workload_pool_id: form[selection!].workload_pool_id,
            }),
            ...(form[selection!].workload_provider_id && {
              workload_provider_id: form[selection!].workload_provider_id,
            }),
            ...(form[selection!].service_account_email && {
              service_account_email: form[selection!].service_account_email,
            }),
          },
          value:
            selection === 'organization' ? org_id : form.individual.project_id,
        });

        setSavedState(undefined);
        setStep(2);
      } catch {
        handleReset();
      }
    }
  };

  const renderAdvancedSettings = (type: SelectionType) => (
    <div className="border-chariot-stroke mt-3 border-t pt-3">
      <button
        type="button"
        className="text-chariot-text-secondary hover:text-chariot-text-primary flex items-center gap-1 text-xs"
        onClick={() => setAdvancedOpen(prev => !prev)}
      >
        <span
          className={`inline-block transition-transform ${advancedOpen ? 'rotate-90' : ''}`}
        >
          ▶
        </span>
        Advanced Settings
      </button>
      {advancedOpen && (
        <div className="mt-2 flex flex-col gap-2">
          <p className="text-chariot-text-secondary text-xs">
            Override only if you used custom names during manual deployment.
          </p>
          <label className="text-chariot-text-primary text-sm font-medium">
            Workload Identity Pool ID
          </label>
          <Input
            className="bg-transparent"
            name="workloadPoolId"
            value={form[type].workload_pool_id}
            onChange={handleInputChange(type, 'workload_pool_id')}
            placeholder="praetorian-chariot-pool"
          />
          <label className="text-chariot-text-primary text-sm font-medium">
            Workload Identity Provider ID
          </label>
          <Input
            className="bg-transparent"
            name="workloadProviderId"
            value={form[type].workload_provider_id}
            onChange={handleInputChange(type, 'workload_provider_id')}
            placeholder="praetorian-chariot-provider"
          />
          <label className="text-chariot-text-primary text-sm font-medium">
            Service Account
          </label>
          <Input
            className="bg-transparent"
            name="serviceAccountEmail"
            value={form[type].service_account_email}
            onChange={handleInputChange(type, 'service_account_email')}
            placeholder="praetorian-chariot or my-sa@other-project.iam.gserviceaccount.com"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="">
      <div className="bg-chariot-stroke mb-6 w-full shrink-0 rounded-lg" />
      {step === 0 && (
        <>
          <div className="text-chariot-text-primary mb-4 font-bold">
            Scope of Integration
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <FeatureRadio
              id="gcp-radio-organization"
              title="Organization"
              description="Integrates all projects within the organization"
              checked={selection === 'organization'}
              onChange={() => setSelection('organization')}
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Organization ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="orgId"
                      value={form.organization.org_id}
                      onChange={handleInputChange('organization', 'org_id')}
                      placeholder="xxxxxxxxxxxx"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Deployment Type
                    </label>
                    <Dropdown
                      styleType="select"
                      options={DEV_TYPE_OPTIONS}
                      selected={getSelectedDropdownOption('organization')}
                      onSelect={handleDropdownSelect('organization')}
                      placeholder="Select deployment type"
                    />
                  </div>
                  {renderAdvancedSettings('organization')}
                </div>
              }
            />
            <FeatureRadio
              id="gcp-radio-individual"
              title="Individual Project"
              description="Integrates a single project"
              checked={selection === 'individual'}
              onChange={() => setSelection('individual')}
              className="flex-1"
              checkedContent={
                <div className="flex w-full flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Organization ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="orgId"
                      value={form.individual.org_id}
                      onChange={handleInputChange('individual', 'org_id')}
                      placeholder="xxxxxxxxxxxx"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Project ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="projectId"
                      value={form.individual.project_id}
                      onChange={handleInputChange('individual', 'project_id')}
                      placeholder="xxxxxxxxxxxx"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-chariot-text-primary text-sm font-medium">
                      Deployment Type
                    </label>
                    <Dropdown
                      styleType="select"
                      options={DEV_TYPE_OPTIONS}
                      selected={getSelectedDropdownOption('individual')}
                      onSelect={handleDropdownSelect('individual')}
                      placeholder="Select deployment type"
                    />
                  </div>
                  {renderAdvancedSettings('individual')}
                </div>
              }
            />
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="px-6"
                onClick={handleCancel}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !selection ||
                  !form[selection].org_id ||
                  (selection === 'individual' && !form.individual.project_id) ||
                  !form[selection].deployment_type ||
                  initializeCloudStatus === 'pending'
                }
                className="px-6"
                size="sm"
              >
                Next
              </Button>
            </div>
          </form>
        </>
      )}
      {step === 1 && (
        <div>
          {form[selection!].deployment_type !== 'manual' ? (
            <>
              {!hasDownloaded && (
                <>
                  <div className="mb-4 flex items-start justify-between">
                    <div className="font-bold">Download Template</div>
                  </div>
                  <div className="text-chariot-text-secondary mb-6 text-sm">
                    To finish Google Cloud Terraform setup, please download the
                    IAC Template, then click finish. For more information,
                    please reference our{' '}
                    <a
                      href="https://docs.praetorian.com/articles/37956600868635-google-cloud-platform-iac-deployment-recommended"
                      className="text-chariot-brand-primary hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Google Cloud documentation
                    </a>
                    .
                  </div>
                </>
              )}
              {hasDownloaded && (
                <>
                  <div className="mb-4 flex items-start justify-between">
                    <div className="font-bold">Verify Deployment</div>
                  </div>
                  <div className="text-chariot-text-secondary mb-6 text-sm">
                    To verify deployment, please enter the infrastructure ID
                    found from the IAC deployment output. For more information,
                    please reference our{' '}
                    <a
                      href="https://docs.praetorian.com/articles/37956600868635-google-cloud-platform-iac-deployment-recommended"
                      className="text-chariot-brand-primary hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Google Cloud documentation
                    </a>
                    .
                  </div>
                  <div className="mb-6">
                    <label className="mb-2 block text-sm font-bold">
                      Infrastructure ID
                    </label>
                    <Input
                      className="bg-transparent"
                      name="infrastructureId"
                      value={infrastructureId}
                      onChange={e => setInfrastructureId(e.target.value)}
                      placeholder="xxxxxxxxxxxx"
                      required
                    />
                  </div>
                  {selection === 'organization' && (
                    <div className="mb-6">
                      <label className="mb-2 block text-sm font-bold">
                        Project ID
                      </label>
                      <Input
                        className="bg-transparent"
                        name="projectId"
                        value={projectId}
                        onChange={e => setProjectId(e.target.value)}
                        placeholder="praetorian-chariot-xxxxxxxx"
                        required
                      />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div>
              <div className="mb-4 font-bold">Manual Setup</div>
              <div className="text-chariot-text-secondary mb-6 text-sm">
                For manual setup, please follow our{' '}
                <a
                  href="https://docs.praetorian.com/articles/37956600868635-google-cloud-platform-iac-deployment-recommended"
                  className="text-chariot-brand-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Google Cloud documentation
                </a>{' '}
                to configure your Google Cloud environment.
              </div>
              {savedState?.response?.username && (
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-bold">
                    Subject
                  </label>
                  <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-3">
                    <code className="text-chariot-text-primary text-sm">
                      {savedState.response.username}
                    </code>
                  </div>
                </div>
              )}
              <div className="mb-6">
                <label className="mb-2 block text-sm font-bold">
                  Infrastructure ID
                </label>
                <Input
                  className="bg-transparent"
                  name="infrastructureId"
                  value={infrastructureId}
                  onChange={e => setInfrastructureId(e.target.value)}
                  placeholder="xxxxxxxxxxxx"
                  required
                />
              </div>
              {selection === 'organization' && (
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-bold">
                    Project ID
                  </label>
                  <Input
                    className="bg-transparent"
                    name="projectId"
                    value={projectId}
                    onChange={e => setProjectId(e.target.value)}
                    placeholder="praetorian-chariot-xxxxxxxx"
                    required
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              className="px-4"
              type="button"
              disabled={
                form[selection!].deployment_type !== 'manual' && hasDownloaded
                  ? !infrastructureId ||
                    (selection === 'organization' && !projectId) ||
                    linkStatus === 'pending'
                  : form[selection!].deployment_type === 'manual'
                    ? !infrastructureId ||
                      (selection === 'organization' && !projectId) ||
                      linkStatus === 'pending'
                    : linkStatus === 'pending'
              }
              onClick={handleFinish}
              size="sm"
            >
              {form[selection!].deployment_type !== 'manual' && !hasDownloaded
                ? 'Download IAC Template'
                : 'Finish'}
            </Button>
          </div>
        </div>
      )}
      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex flex-col items-center justify-center py-12 text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-chariot-text-primary mb-2 text-2xl font-bold"
          >
            Hooray! You&apos;re All Set!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-chariot-text-secondary mb-8 text-sm"
          >
            Your Google Cloud setup is complete. Chariot will now scan and
            report on this environment
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Button onClick={handleCancel} size="sm" className="px-8">
              Done
            </Button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
